import { useState, useCallback } from 'react';

/**
 * useActivityTracker Hook
 * 
 * Simple hook to track user activity timestamp.
 * Use this to update a timestamp whenever the user makes changes,
 * which can then be passed to useAutoSave for smart debouncing.
 * 
 * Usage:
 * ```tsx
 * const { activityTimestamp, recordActivity } = useActivityTracker();
 * 
 * // In your change handlers:
 * const handleChange = (value) => {
 *   setValue(value);
 *   recordActivity(); // Record that user is active
 * };
 * 
 * // Pass to useAutoSave:
 * useAutoSave({
 *   hasUnsavedChanges,
 *   saveHandler,
 *   activityTimestamp, // Delays save while user is typing
 * });
 * ```
 */
export function useActivityTracker() {
  const [activityTimestamp, setActivityTimestamp] = useState<number>(0);

  const recordActivity = useCallback(() => {
    setActivityTimestamp(Date.now());
  }, []);

  return {
    activityTimestamp,
    recordActivity,
  };
}

export default useActivityTracker;
